'use client';

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import styles from './sortableMediaGrid.module.scss';

export type SortableMediaItem = {
  src: string;
  type?: 'image' | 'video' | 'pdf';
  alt?: string | null;
};

export const buildMediaDragIds = (media: { src: string }[]): string[] => {
  const seen = new Map<string, number>();
  return media.map((item) => {
    const occurrence = seen.get(item.src) ?? 0;
    seen.set(item.src, occurrence + 1);
    return occurrence === 0 ? item.src : `${item.src}#${occurrence}`;
  });
};

export const reorderMediaByDragIds = <T extends { src: string }>(
  media: T[],
  activeId: string,
  overId: string
): T[] | null => {
  if (activeId === overId) return null;

  const dragIds = buildMediaDragIds(media);
  const oldIndex = dragIds.indexOf(activeId);
  const newIndex = dragIds.indexOf(overId);
  if (oldIndex === -1 || newIndex === -1) return null;

  return arrayMove(media, oldIndex, newIndex);
};

const DragGripIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="currentColor"
    aria-hidden="true"
  >
    <circle cx="4" cy="3" r="1.5" />
    <circle cx="10" cy="3" r="1.5" />
    <circle cx="4" cy="7" r="1.5" />
    <circle cx="10" cy="7" r="1.5" />
    <circle cx="4" cy="11" r="1.5" />
    <circle cx="10" cy="11" r="1.5" />
  </svg>
);

type MediaPreviewProps = {
  item: SortableMediaItem;
  altFallback: string;
  interactiveVideo: boolean;
};

const MediaPreview = ({
  item,
  altFallback,
  interactiveVideo
}: MediaPreviewProps) => {
  const mediaType = item.type ?? 'image';

  if (mediaType === 'video') {
    return (
      <video
        src={item.src}
        controls={interactiveVideo}
        muted={!interactiveVideo}
        playsInline
        preload="metadata"
        className={styles.mediaVideo}
        onPointerDown={
          interactiveVideo ? (event) => event.stopPropagation() : undefined
        }
        onKeyDown={
          interactiveVideo ? (event) => event.stopPropagation() : undefined
        }
      />
    );
  }

  if (mediaType === 'pdf') {
    return (
      <div className={styles.mediaPdf}>
        <span>PDF</span>
      </div>
    );
  }

  return (
    <img
      src={item.src}
      alt={item.alt || altFallback}
      draggable={false}
      className={styles.mediaImage}
    />
  );
};

type SortableMediaCardProps = {
  id: string;
  item: SortableMediaItem;
  position: number;
  total: number;
  altFallback: string;
  disabled: boolean;
  onRemove: () => void;
};

const SortableMediaCard = ({
  id,
  item,
  position,
  total,
  altFallback,
  disabled,
  onRemove
}: SortableMediaCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.mediaCard} ${disabled ? '' : styles.mediaCardDraggable}`}
      aria-label={`${item.alt || altFallback}, position ${position} of ${total}. Press space to start reordering.`}
      {...attributes}
      {...listeners}
    >
      <div className={styles.mediaPreview}>
        <MediaPreview item={item} altFallback={altFallback} interactiveVideo />
        {!disabled && (
          <span className={styles.dragHandle} aria-hidden="true">
            <DragGripIcon />
          </span>
        )}
        <button
          type="button"
          className={styles.removeButton}
          onClick={onRemove}
          onPointerDown={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          disabled={disabled}
          aria-label="Remove media"
        >
          &times;
        </button>
      </div>
    </div>
  );
};

export type SortableMediaGridProps<T extends SortableMediaItem> = {
  media: T[];
  onReorder: (media: T[]) => void;
  onRemove: (index: number) => void;
  altFallback: string;
  disabled?: boolean;
  children?: ReactNode;
};

export function SortableMediaGrid<T extends SortableMediaItem>({
  media,
  onReorder,
  onRemove,
  altFallback,
  disabled = false,
  children
}: SortableMediaGridProps<T>) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 8 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const dragIds = useMemo(() => buildMediaDragIds(media), [media]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      if (!over) return;

      const reordered = reorderMediaByDragIds(
        media,
        String(active.id),
        String(over.id)
      );
      if (reordered) onReorder(reordered);
    },
    [media, onReorder]
  );

  const handleDragCancel = useCallback(() => setActiveId(null), []);

  const activeIndex = activeId ? dragIds.indexOf(activeId) : -1;
  const activeItem = activeIndex === -1 ? null : media[activeIndex];

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={dragIds} strategy={rectSortingStrategy}>
        <div className={styles.mediaGrid}>
          {media.map((item, index) => (
            <SortableMediaCard
              key={dragIds[index]}
              id={dragIds[index]!}
              item={item}
              position={index + 1}
              total={media.length}
              altFallback={altFallback}
              disabled={disabled}
              onRemove={() => onRemove(index)}
            />
          ))}
          {children}
        </div>
      </SortableContext>

      {activeItem &&
        createPortal(
          <DragOverlay style={{ zIndex: 'var(--z-index-drag-overlay, 1250)' }}>
            <div className={`${styles.mediaCard} ${styles.mediaCardOverlay}`}>
              <div className={styles.mediaPreview}>
                <MediaPreview
                  item={activeItem}
                  altFallback={altFallback}
                  interactiveVideo={false}
                />
              </div>
            </div>
          </DragOverlay>,
          document.body
        )}
    </DndContext>
  );
}
